// Picking files from OneDrive, for an import: Microsoft's sign-in, through MSAL, and Microsoft's
// picker, which lets the person choose files, then a token for the API, which does the copying.
//
// Two tokens, both for the account signed in once. The picker's own ("OneDrive.ReadOnly") is handed
// to the picker and nothing else, since Graph refuses it. The API gets one for Graph ("Files.Read"),
// asked for afresh on every pick so it has close to its full hour left, with how long that is: unlike
// Google's, Microsoft's tokens can't be asked about. Microsoft won't issue both scopes at once.
//
// Signing in and the picker are each a popup, and a browser lets through one popup per click. So the
// first time, or whenever Microsoft wants the person to sign in again, the click only signs in, and
// "pickFromOneDrive" says so; picking takes one more click. Microsoft asking for a fresh sign-in is
// only found once the picker's popup is open, so that takes a click to sign in, and one more to pick. The sign-in is kept in local storage,
// encrypted by MSAL, so every import after that is one click. The picker's popup is opened straight
// away on that click, before the tokens are asked for, so nothing the click waits on can lose it.
//
// The picker is Microsoft's page, opened by posting its token to it from a blank popup, and talked
// to over a message channel it offers once it has loaded: it asks for tokens when it needs them,
// and says what was picked, or that it was closed. Personal accounts only, for now: a work or school
// account's picker lives on its own SharePoint, which nothing here finds yet.
//
// Sign-in comes back to the top of the drive in its popup, the address Microsoft has registered for
// it. "finishMicrosoftSignIn" hands that answer to the drive that opened it, and is checked before
// the app renders.
import {InteractionRequiredAuthError, PublicClientApplication} from '@azure/msal-browser'

const AUTHORITY = 'https://login.microsoftonline.com/consumers'
const PICKER_ORIGIN = 'https://onedrive.live.com'
const PICKER_SCOPES = ['OneDrive.ReadOnly']
const GRAPH_SCOPES = ['https://graph.microsoft.com/Files.Read']
const PICKER_WINDOW = 'width=1000,height=700'
// A popup closed with its own button sends nothing, so it is looked in on.
const CLOSED_CHECK_INTERVAL = 500

let preparing = null
let signInNeeded = false

/** Readies MSAL once, and again only after a start that failed. */
export const prepareOneDrivePicker = () => {
  preparing ??= (async () => {
    const app = new PublicClientApplication({
      auth: {clientId: MICROSOFT_CLIENT_ID, authority: AUTHORITY, redirectUri: window.location.origin},
      cache: {cacheLocation: 'localStorage'}
    })

    await app.initialize()

    return app
  })().catch(error => {
    preparing = null

    throw error
  })

  return preparing
}

/**
 * Whether this window is Microsoft's sign-in popup come back, with its answer in the fragment. Told by
 * the address alone: Microsoft's pages cut the popup off from its opener, so MSAL hands the answer
 * over by broadcast, not through "window.opener".
 */
export const isMicrosoftSignIn = () => {
  const answer = new URLSearchParams(window.location.hash.slice(1))

  return window.location.pathname === '/' && answer.has('state') && (answer.has('code') || answer.has('error'))
}

export const finishMicrosoftSignIn = () => import('@azure/msal-browser/redirect-bridge')
  .then(({broadcastResponseToMainFrame}) => broadcastResponseToMainFrame())

const tokenFor = (app, scopes, forceRefresh = false) => app.acquireTokenSilent({scopes, account: app.getAllAccounts()[0], forceRefresh})

const secondsUntil = date => Math.max(0, Math.floor((date.getTime() - Date.now()) / 1000))

const openPicker = (picker, accessToken, channelId) => {
  const options = {
    sdk: '8.0',
    entry: {oneDrive: {files: {}}},
    authentication: {},
    messaging: {origin: window.location.origin, channelId},
    typesAndSources: {mode: 'files', pivots: {oneDrive: true, recent: true}},
    selection: {mode: 'multiple'}
  }
  const query = new URLSearchParams({filePicker: JSON.stringify(options), locale: 'en-us'})
  const form = picker.document.createElement('form')
  const token = picker.document.createElement('input')

  form.action = `${PICKER_ORIGIN}/picker?${query}`
  form.method = 'POST'
  token.type = 'hidden'
  token.name = 'access_token'
  token.value = accessToken
  form.append(token)
  picker.document.body.append(form)
  form.submit()
}

// The picked files, as the API names them, none when the picker was closed.
const choose = (app, picker, accessToken) => new Promise(resolve => {
  const channelId = crypto.randomUUID()
  const listening = new AbortController()
  let port = null

  const finish = items => {
    listening.abort()
    picker.close()
    resolve(items.map(({id, parentReference}) => `${parentReference.driveId}:${id}`))
  }

  const answer = async ({type, id, data}) => {
    if (type !== 'command') return

    port.postMessage({type: 'acknowledge', id})

    if (data.command === 'authenticate') {
      const {accessToken: token} = await tokenFor(app, PICKER_SCOPES)

      port.postMessage({type: 'result', id, data: {result: 'token', token}})
    } else if (data.command === 'pick') {
      port.postMessage({type: 'result', id, data: {result: 'success'}})
      finish(data.items)
    } else if (data.command === 'close') {
      finish([])
    } else {
      port.postMessage({type: 'result', id, data: {result: 'error', error: {code: 'unsupportedCommand', message: data.command}, isExpected: true}})
    }
  }

  const onMessage = event => {
    if (event.source !== picker || event.origin !== PICKER_ORIGIN) return
    if (event.data?.type !== 'initialize' || event.data.channelId !== channelId) return

    port = event.ports[0]
    port.addEventListener('message', message => answer(message.data).catch(() => finish([])))
    port.start()
    port.postMessage({type: 'activate'})
  }

  const closedCheck = setInterval(() => picker.closed && finish([]), CLOSED_CHECK_INTERVAL)

  listening.signal.addEventListener('abort', () => clearInterval(closedCheck))
  window.addEventListener('message', onMessage, {signal: listening.signal})
  openPicker(picker, accessToken, channelId)
})

/**
 * The Graph token, how many seconds it has left, and the ids of the files picked, none when the
 * picker was closed. Instead, "{signedIn: true}" when the click went on signing in, and choosing
 * files needs another, or "{signInNeeded: true}" when Microsoft wants the person to sign in again,
 * which needs another click to begin. Rejects when Microsoft couldn't be reached, the picker's popup
 * was blocked, or the person didn't sign in.
 */
export const pickFromOneDrive = async () => {
  const app = await prepareOneDrivePicker()

  if (signInNeeded || !app.getAllAccounts().length) {
    await app.loginPopup({scopes: GRAPH_SCOPES})
    signInNeeded = false

    return {signedIn: true}
  }

  const picker = window.open('', 'onedrive-picker', PICKER_WINDOW)

  if (!picker) throw new Error('The OneDrive picker was blocked')

  try {
    const pickerToken = await tokenFor(app, PICKER_SCOPES)
    const graphToken = await tokenFor(app, GRAPH_SCOPES, true)
    const fileIds = await choose(app, picker, pickerToken.accessToken)

    return {accessToken: graphToken.accessToken, expiresIn: secondsUntil(graphToken.expiresOn), fileIds}
  } catch (error) {
    picker.close()

    if (!(error instanceof InteractionRequiredAuthError)) throw error

    signInNeeded = true

    return {signInNeeded: true}
  }
}
